import {
  BoltIcon,
  MapPinIcon,
  SignalIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  ArrowsRightLeftIcon,
  NoSymbolIcon,
  PowerIcon,
  ClockIcon,
  BellAlertIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';

/**
 * One description of every alert type, shared by every screen that shows one.
 *
 * The alerts page and the dashboard each carried their own copy, and they had
 * already drifted: the alerts page was missing `sos` and both org-zone types
 * (so those rendered as raw enum identifiers and could not be filtered), while
 * the dashboard drew the same bell icon for every type regardless of what had
 * happened. Keeping one table means a new alert type is described once.
 *
 * Must stay in step with `alertTypeEnum` in backend/src/models/schema.ts.
 */

export interface AlertTypeMeta {
  /** Arabic label shown to operators. */
  name: string;
  icon: React.ReactNode;
  /** Text colour for the icon. */
  color: string;
}

const icon = (C: React.ComponentType<{ className?: string }>) => <C className="w-5 h-5" />;

export const ALERT_TYPE_META: Record<string, AlertTypeMeta> = {
  speed_limit:         { name: 'تجاوز السرعة',      icon: icon(BoltIcon),                  color: 'text-warning-500' },
  geofence_enter:      { name: 'دخول منطقة',        icon: icon(MapPinIcon),                color: 'text-primary-500' },
  geofence_exit:       { name: 'خروج من منطقة',     icon: icon(MapPinIcon),                color: 'text-orange-500' },
  sos:                 { name: 'نداء استغاثة',      icon: icon(BellAlertIcon),             color: 'text-emergency-600' },
  low_battery:         { name: 'بطارية منخفضة',     icon: icon(BoltIcon),                  color: 'text-yellow-500' },
  connection_lost:     { name: 'انقطاع الاتصال',    icon: icon(SignalIcon),                color: 'text-gray-500' },
  connection_restored: { name: 'استعادة الاتصال',   icon: icon(SignalIcon),                color: 'text-success-500' },
  maintenance_due:     { name: 'موعد صيانة',        icon: icon(WrenchScrewdriverIcon),     color: 'text-purple-500' },
  crash:               { name: 'حادث',              icon: icon(ExclamationTriangleIcon),   color: 'text-emergency-600' },
  towing:              { name: 'قطر المركبة',       icon: icon(ArrowsRightLeftIcon),       color: 'text-orange-600' },
  jamming:             { name: 'تشويش شبكة',        icon: icon(NoSymbolIcon),              color: 'text-red-500' },
  gnss_jamming:        { name: 'تشويش GPS',         icon: icon(NoSymbolIcon),              color: 'text-red-400' },
  unplug:              { name: 'فصل الجهاز',        icon: icon(PowerIcon),                 color: 'text-red-600' },
  power_cut:           { name: 'انقطاع التيار',     icon: icon(ShieldExclamationIcon),     color: 'text-red-700' },
  idling:              { name: 'تشغيل بدون حركة',   icon: icon(ClockIcon),                 color: 'text-gray-500' },
  org_zone_exit:       { name: 'خروج عن نطاق الجهة', icon: icon(MapPinIcon),               color: 'text-emergency-600' },
  org_zone_return:     { name: 'عودة إلى النطاق',   icon: icon(MapPinIcon),                color: 'text-success-600' },
};

export const alertTypeName = (type?: string | null): string =>
  (type && ALERT_TYPE_META[type]?.name) || type || 'غير معروف';

export const alertTypeIcon = (type?: string | null): React.ReactNode =>
  (type && ALERT_TYPE_META[type]?.icon) || icon(BellAlertIcon);

export const alertTypeColor = (type?: string | null): string =>
  (type && ALERT_TYPE_META[type]?.color) || 'text-gray-500';

export interface SeverityMeta {
  name: string;
  color: string;
  bgColor: string;
  /** Solid colour for charts. */
  chart: string;
  /** Filled chip, for places that need contrast rather than a tint. */
  solid: string;
}

export const SEVERITY_META: Record<string, SeverityMeta> = {
  critical: { name: 'حرج',   color: 'text-emergency-600', bgColor: 'bg-emergency-50', chart: '#dc2626', solid: 'bg-emergency-600 text-white' },
  high:     { name: 'عالي',  color: 'text-orange-600',    bgColor: 'bg-orange-50',    chart: '#f97316', solid: 'bg-orange-500 text-white' },
  medium:   { name: 'متوسط', color: 'text-warning-600',   bgColor: 'bg-warning-50',   chart: '#eab308', solid: 'bg-warning-500 text-white' },
  low:      { name: 'منخفض', color: 'text-success-600',   bgColor: 'bg-success-50',   chart: '#22c55e', solid: 'bg-success-500 text-white' },
};

/**
 * Alerts that mean someone is interfering with the vehicle or it has been in
 * an accident — as opposed to the routine operational noise.
 *
 * These are the ones an operator must not scroll past, so they get a distinct
 * treatment wherever alerts are listed, sort to the top, and ring a sound.
 * Defined once here so the lists, the sound and the dashboard card cannot
 * disagree about what counts as urgent.
 */
export const TAMPER_TYPES = new Set(['unplug', 'power_cut', 'crash']);

export const isTamperAlert = (type?: string | null): boolean =>
  Boolean(type && TAMPER_TYPES.has(type));

/** True when an alert should be surfaced loudly: tampering, or any critical. */
export const isUrgentAlert = (a: { type?: string | null; severity?: string | null }): boolean =>
  isTamperAlert(a.type) || a.severity === 'critical';
